import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, Sun, User as UserIcon } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useLogout } from "@/hooks/mutations/use-logout";
import { useUserProfile } from "@/hooks/queries/use-user-profile";
import { setSkipOnboarding } from "@/stores/auth-store";

export const Route = createFileRoute("/bem-vindo")({
	component: WelcomePage,
});

function WelcomePage() {
	const navigate = useNavigate();
	const { data: user, isLoading } = useUserProfile();
	const { mutate: logout } = useLogout();

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-white">
				<Loader2 className="size-10 animate-spin text-slate-300" />
			</div>
		);
	}

	const firstName = user?.name?.split(" ")[0] || "Estudante";
	const today = new Intl.DateTimeFormat("pt-BR", {
		day: "numeric",
		month: "long",
		year: "numeric",
	}).format(new Date());

	return (
		<div className="relative bg-white min-h-screen">
			<div className="mx-auto max-w-7xl lg:grid lg:grid-cols-12 lg:gap-x-8">
				<div className="flex justify-center col-span-12 min-h-screen lg:items-center">
					<div className="mx-auto max-w-md px-6 lg:px-0 w-full">
						<div className="flex flex-col justify-center h-full space-y-12">
							<div className="flex flex-col items-center">
								<Avatar className="size-32 border-4 border-white shadow-xl">
									<AvatarImage
										src={user?.avatar || ""}
										className="object-cover"
									/>
									<AvatarFallback className="bg-slate-50 text-slate-300">
										<UserIcon className="size-16" />
									</AvatarFallback>
								</Avatar>

								<h1 className="text-pretty text-2xl lg:text-3xl font-medium tracking-tight text-slate-900 text-center mt-6">
									Bem-vinda de volta, {firstName}{" "}
									<img
										src="/img/gifs/grinning-face-with-sweat.gif"
										className="inline-flex size-10 align-top"
										alt="Emoji"
									/>
								</h1>

								<div className="flex items-center gap-4 bg-slate-50 rounded-md p-6 mt-10 w-full border border-slate-100 shadow-sm">
									<div className="flex items-center justify-center size-12 bg-white rounded-md shadow-sm border border-slate-100 shrink-0">
										<Sun className="size-7 text-primary" />
									</div>
									<div className="flex flex-col">
										<p className="text-sm font-semibold text-slate-900 leading-tight">
											Que o dia hoje seja tão incrível quanto você! 🌟
										</p>
										<p className="text-xs text-slate-500 mt-1 capitalize">
											{today}
										</p>
									</div>
								</div>

								<div className="flex flex-col gap-4 mt-12 w-full">
									<Button
										className="w-full h-12 rounded-md text-base font-semibold shadow-lg shadow-primary/20"
										onClick={() => {
											setSkipOnboarding();
											navigate({ to: "/painel" });
										}}
									>
										Acessar meu espaço
									</Button>
									<Button
										variant="outline"
										className="w-full h-12 rounded-md text-base font-semibold border-slate-200 text-slate-600 hover:bg-slate-50"
										onClick={() => navigate({ to: "/onboarding/apelido" })}
									>
										Personalizar
									</Button>
								</div>

								<div className="mt-12 text-center text-sm text-slate-500">
									Não é {firstName}?{" "}
									<button
										type="button"
										onClick={() => logout()}
										className="font-semibold text-primary hover:underline"
									>
										Clique aqui
									</button>
								</div>
							</div>

							<div className="lg:hidden w-full border-t border-slate-100 pt-6 flex justify-center">
								<svg
									width="40"
									height="25"
									viewBox="0 0 40 25"
									fill="none"
									className="opacity-20"
									role="img"
								>
									<title>Decoração de boas-vindas</title>
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M10.7028 2.19308C11.2723 2.22825 11.7608 1.77737 11.8732 1.23C11.993 0.649268 11.7165 0 10.9434 0C10.4264 0 9.87346 0.37874 9.80526 1.09835C9.75089 1.66195 10.1232 2.15701 10.7038 2.19308H10.7028ZM12.0015 16.5288C10.6216 15.9036 8.99693 15.5752 7.71157 15.4028C6.42622 15.2304 3.72906 15.0689 2.11987 15.3692C2.71024 9.92533 3.06865 3.57142 3.19509 1.10352C3.22238 0.570024 2.84942 0.0846109 2.31999 0.0102112C1.71598 -0.0741689 1.18382 0.374951 1.1529 0.967427C1.05374 2.91361 0.326012 11.9595 0.00490031 15.91C-0.0696921 16.8264 0.718077 17.5794 1.63229 17.4642C2.31454 17.3789 3.16871 17.3063 4.15479 17.2764C5.14086 17.2464 6.33889 17.3181 6.72459 17.3535C8.91778 17.5549 10.3751 18.0285 11.1919 18.377C11.6695 18.5802 12.2226 18.3897 12.4737 17.9369C12.7602 17.4197 12.541 16.7719 12.0015 16.5279V16.5288ZM38.0136 7.99374C38.2439 7.47617 38.8458 7.21239 39.3685 7.43251L39.3676 7.4316C39.8984 7.65536 40.1406 8.26389 39.9166 8.78691C39.0389 10.8317 37.1558 14.7193 32.3718 14.7193C31.7626 14.7193 31.1644 14.6448 30.5935 14.4929C28.9317 14.0499 27.6132 12.9347 27.0641 11.3265C25.7211 13.0966 24.1185 14.5001 22.3302 15.1732C22.6552 16.3794 22.7645 17.7883 22.6024 19.3683C22.2537 22.8348 20.2131 24.7104 18.0114 24.4139C16.0309 24.1474 14.6177 22.1481 15.1377 19.684C15.8415 16.3612 17.7264 14.8285 19.5429 13.9425C19.3381 13.6733 19.1214 13.4577 18.9001 13.2904C18.6096 13.0702 18.4403 12.7264 18.4648 12.3635L18.5113 11.6722C18.5532 11.0464 18.9056 10.477 19.4592 10.1795C20.5591 9.58918 21.7556 8.45218 21.7556 6.29915C21.7556 5.42047 21.4333 4.98659 20.7686 4.83105C19.6149 4.55817 18.1125 5.61149 17.048 7.86821L16.9879 7.84002L17.0489 7.86912C16.9902 7.99137 16.9159 8.15753 16.8266 8.35722C16.0004 10.205 13.8903 14.9238 10.9755 14.2909C9.80636 14.0399 9.08885 13.1012 9.01236 11.7813C8.90856 10.0103 9.49222 4.52906 9.52591 4.23435C9.59147 3.67039 10.1087 3.2638 10.6696 3.32565C11.2305 3.38751 11.6275 3.9078 11.5829 4.46902C11.5008 5.50357 11.4177 6.41044 11.3438 7.2157C11.2145 8.62651 11.1138 9.7254 11.0975 10.6525C11.0802 11.6531 11.7668 12.4672 12.8066 11.0254C13.6307 9.88298 14.6068 8.22569 15.1969 6.99772C16.3514 4.54907 18.5013 2.1932 21.2384 2.84083C22.9976 3.25379 23.7816 4.78284 23.8053 6.26822C23.8371 8.27662 22.6443 10.7662 20.571 12.026C20.9425 12.3817 21.273 12.8037 21.5525 13.2794C23.3391 12.6163 25.2093 10.7216 26.6617 8.22023C26.9576 7.71085 27.2062 7.176 27.432 6.63206C27.4566 6.57118 27.4829 6.51029 27.5093 6.4494L27.5094 6.44923C28.9189 3.24288 31.3483 1.99308 33.3561 2.86357C35.0789 3.61036 35.8537 5.72428 34.9277 7.77725C34.0745 9.65922 31.5168 10.8208 28.8133 9.56553C28.8051 11.5412 30.2866 12.6746 32.3736 12.6746C35.8137 12.6746 37.1294 9.98122 38.0136 7.99374ZM32.5411 4.74009C31.9839 4.49904 30.5807 4.54998 29.3851 7.27151H29.3861C29.3547 7.34325 29.3512 7.35035 29.348 7.35761C29.3442 7.36618 29.341 7.37498 29.2932 7.49073C31.2554 8.65138 32.6641 7.81091 33.062 6.93314C33.5956 5.74884 33.0929 4.97931 32.5411 4.74009ZM18.2855 22.3882C18.942 22.4764 20.2778 22.0289 20.5664 19.1618C20.7003 17.8557 20.6101 16.7268 20.3825 15.8127C19.0312 16.4722 17.6927 17.5146 17.1427 20.1078C16.8249 21.6141 17.6162 22.2982 18.2855 22.3882Z"
										fill="currentColor"
									/>
								</svg>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
